import i18n from 'i18n/config';
import { store } from 'redux/store';
import { translate } from 'hooks/useTranslate';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { HoldingClass } from 'types';

import { formatNumber } from 'utils/formats';

import { getHoldingClasses } from 'modules/partners/api/getHoldingClasses';
import { isShareClassVisible } from 'modules/trading/utils/isShareClassVisible';
import { getHoldingClassById } from 'modules/partners/api/getHoldingClassById';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';

type UseGetHoldingClassOptionsProps = {
  societyId?: string;
  holdingClassId?: string | null;
  partnerId?: string;
  reservedSharesByShareClass?: Record<string, number>;
  includeShares?: boolean;
  autoSelectFirst?: boolean;
  includeNumerationOption?: boolean;
};

const FIELDS = 'name sharesCount';
const FIELDS_WITH_NUMERATION = 'name sharesCount numerationOption';
const FIELD_TO_POPULATE = ['numerationOption'];

const labelByPlatform = (
  holdingClass: HoldingClass,
  partnerId?: string,
  reservedSharesByShareClass?: Record<string, number>,
  includeShares = true
): string => {
  const { name, sharesCount, partnerCurrentSharesCount } = holdingClass;

  const gross = partnerId
    ? partnerCurrentSharesCount || 0
    : sharesCount?.actual || 0;
  const reserved =
    partnerId && reservedSharesByShareClass
      ? reservedSharesByShareClass[holdingClass._id as string] || 0
      : 0;
  const sharesTotal = partnerId ? Math.max(0, gross - reserved) : gross;

  const sharesSuffix = translate('shares');

  if (!includeShares) {
    return name;
  }

  if (isShareClassVisible()) {
    return `${name} | ${formatNumber(sharesTotal)} ${sharesSuffix}`;
  }

  return `${formatNumber(sharesTotal)} ${sharesSuffix}`;
};

const mapOption = (
  holdingClass: HoldingClass,
  partnerId?: string,
  reservedSharesByShareClass?: Record<string, number>,
  includeShares = true
): CustomAsyncSelectOption => {
  const label = labelByPlatform(
    holdingClass,
    partnerId,
    reservedSharesByShareClass,
    includeShares
  );
  const gross = holdingClass.partnerCurrentSharesCount || 0;
  const reserved =
    partnerId && reservedSharesByShareClass
      ? reservedSharesByShareClass[holdingClass._id as string] || 0
      : 0;
  const net =
    partnerId && reservedSharesByShareClass
      ? Math.max(0, gross - reserved)
      : holdingClass.partnerCurrentSharesCount;

  return {
    name: holdingClass.name,
    value: holdingClass._id,
    label,
    sharesCount: net ?? holdingClass.sharesCount?.actual ?? 0,
    numerationOptionName:
      (holdingClass?.numerationOption as HoldingClass)?.name || '1',
  };
};

const mapOptions = (
  holdingClasses: HoldingClass[],
  partnerId?: string,
  reservedSharesByShareClass?: Record<string, number>,
  includeShares = true
): CustomAsyncSelectOption[] =>
  holdingClasses
    ?.filter(
      (holdingClass: HoldingClass) =>
        (holdingClass?.sharesCount?.actual || 0) > 0
    )
    .map((holdingClass: HoldingClass) =>
      mapOption(
        holdingClass,
        partnerId,
        reservedSharesByShareClass,
        includeShares
      )
    );

export const useGetHoldingClassOptions = ({
  societyId,
  holdingClassId,
  partnerId,
  reservedSharesByShareClass,
  includeShares = true,
  autoSelectFirst = true,
  includeNumerationOption = false,
}: UseGetHoldingClassOptionsProps) => {
  const [language, setLanguage] = useState(i18n.language);
  const [selectedHoldingClass, setSelectedHoldingClass] = useState<string>('');
  const [selectedHoldingClassOption, setSelectedHoldingClassOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [defaultHoldingClassOption, setDefaultHoldingClassOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [holdingClassOptions, setHoldingClassOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);
  const [fetchedHoldingClasses, setFetchedHoldingClasses] = useState<
    HoldingClass[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fields = includeNumerationOption ? FIELDS_WITH_NUMERATION : FIELDS;
  const fieldsToPopulate = useMemo(
    () => (includeNumerationOption ? FIELD_TO_POPULATE : []),
    [includeNumerationOption]
  );

  useEffect(() => {
    const handleLanguageChanged = (lng: string) => setLanguage(lng);
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  const handleChangeSelectedHoldingClass = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setSelectedHoldingClass(selectedOption?.value || '');
    setSelectedHoldingClassOption(selectedOption || null);
  };

  const handleSearchHoldingClass = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm || !societyId) return;

      try {
        const holdingClasses = await store.dispatch(
          getHoldingClasses({
            societyId,
            partnerId,
            params: { fields, fieldsToPopulate, searchTerm },
          })
        );

        return mapOptions(
          holdingClasses,
          partnerId,
          reservedSharesByShareClass,
          includeShares
        );
      } catch (error) {
        setIsError(true);
      }
    },
    [
      societyId,
      partnerId,
      reservedSharesByShareClass,
      includeShares,
      fields,
      fieldsToPopulate,
    ]
  );

  const fetchHoldingClasses = useCallback(async () => {
    if (!societyId) return;

    setIsLoading(true);
    setIsError(false);

    try {
      if (holdingClassId) {
        const holdingClass = await store.dispatch(
          getHoldingClassById({
            holdingClassId,
            partnerId,
          })
        );

        setFetchedHoldingClasses([holdingClass]);
        setIsLoading(false);
        return;
      }

      const holdingClasses = await store.dispatch(
        getHoldingClasses({
          societyId,
          partnerId,
          params: { fields, fieldsToPopulate },
        })
      );

      setFetchedHoldingClasses(holdingClasses);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  }, [holdingClassId, partnerId, societyId, fields, fieldsToPopulate]);

  useEffect(() => {
    fetchHoldingClasses();
  }, [fetchHoldingClasses]);

  const displayOptions = useMemo(() => {
    if (!fetchedHoldingClasses?.length) return [];
    if (holdingClassId) {
      const hc = fetchedHoldingClasses[0];
      return [
        mapOption(hc, partnerId, reservedSharesByShareClass, includeShares),
      ];
    }
    return mapOptions(
      fetchedHoldingClasses,
      partnerId,
      reservedSharesByShareClass,
      includeShares
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchedHoldingClasses,
    partnerId,
    reservedSharesByShareClass,
    holdingClassId,
    includeShares,
    language,
  ]);

  useEffect(() => {
    if (holdingClassId) {
      const opt = displayOptions[0] || null;
      setSelectedHoldingClassOption(opt);
      setDefaultHoldingClassOption(opt);
      setSelectedHoldingClass(opt?.value ? String(opt.value) : '');
      return;
    }

    setHoldingClassOptions(displayOptions);

    if (!autoSelectFirst) {
      setSelectedHoldingClassOption(null);
      setDefaultHoldingClassOption(null);
      setSelectedHoldingClass('');
      return;
    }

    setDefaultHoldingClassOption((prev) => prev || displayOptions[0] || null);
    setSelectedHoldingClassOption((prev) => {
      const id = prev?.value as string | undefined;
      return (
        displayOptions.find((o) => o.value === id) || displayOptions[0] || null
      );
    });
    setSelectedHoldingClass((prev) => {
      const next =
        displayOptions.find((o) => o.value === prev) ||
        displayOptions[0] ||
        null;
      return next?.value ? String(next.value) : '';
    });
  }, [displayOptions, holdingClassId, autoSelectFirst]);

  return {
    data: {
      selectedHoldingClass,
      selectedHoldingClassOption,
      holdingClassOptions,
      defaultHoldingClassOption,
    },
    actions: {
      handleChangeSelectedHoldingClass,
      handleSearchHoldingClass,
    },
    isLoading,
    isError,
  };
};
